// @ts-expect-error Not yet typed.
import FlatPickr from 'vue-flatpickr-component';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Settings } from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { sort } from '@codegrade/utils';
import { Icon } from '@/components/Icon';
import Loader from '@/components/Loader';
import PromiseLoader from '@/components/PromiseLoader';
import { WEEKDAY_LOOKUP } from '@/models/calendar_config';
import { UserStore } from '@/store';
import * as utils from '@/utils';
import './style.less';
export default tsx.component({
    name: 'first-day-of-week-dropdown',
    props: {
        calendarConfig: p.ofType().required,
    },
    /** @returns The initial state of this component. */
    data() {
        const uniq = utils.getUniqueId();
        return {
            loading: true,
            loadError: Maybe.empty(),
            setPromise: Maybe.empty(),
            buttonId: `calendar-settings-button-${uniq}`,
            modalId: `calendar-settings-modal-${uniq}`,
            firstDayOfWeekId: `first-day-of-week-${uniq}`,
        };
    },
    /** Set up the loader for the calendar config. */
    created() {
        utils.watchedLoad({
            instance: this,
            getValues: () => ({
                userId: this.currentUserId,
            }),
            load: async ({ userId }) => {
                if (userId > 0) {
                    await this.calendarConfig.load();
                }
            },
            reset: () => {
                this.loadError = Maybe.empty();
                this.loading = true;
            },
            setError: err => {
                this.loadError = Maybe.of(err);
                this.loading = false;
            },
            setResult: () => {
                this.loading = false;
            },
        });
    },
    computed: {
        /** @returns The id of the logged in user. */
        currentUserId() {
            return UserStore.id();
        },
        /** @returns The currently loaded `CalendarConfig` user setting. */
        currentCalendarConfig() {
            return this.calendarConfig.get();
        },
        /** @returns The currently configured first day of the week. */
        firstDayOfWeek() {
            return this.currentCalendarConfig.firstDayOfWeek;
        },
        /** @returns The FlatPickr configuration. */
        flatpickrConfig() {
            return {
                static: true,
                enableTime: true,
                time_24hr: true,
                minuteIncrement: 1,
                locale: {
                    firstDayOfWeek: this.firstDayOfWeek,
                },
                mode: 'single',
            };
        },
        /** @returns The modal with the configuration options. */
        modalNode() {
            return (<b-modal id={this.modalId} bodyClass="calendar-settings-modal" size="lg" title="Calendar configuration" okOnly okTitle="Close" okVariant="secondary">
                    <div class="d-flex flex-row">
                        <div class="flex-grow-1">{this.firstDayOfWeekNode}</div>
                        <div class="flex-grow-0 ml-3">{this.calendarNode}</div>
                    </div>
                </b-modal>);
        },
        /** @returns The sorted list of days to render in the radio group. */
        sortedWeekdays() {
            return sort.sortBy(Object.values(WEEKDAY_LOOKUP), day => [day.order]);
        },
        /** @returns The `firstDayOfWeek` checkboxes. */
        firstDayOfWeekNode() {
            return (<b-form-group label="First day of week" labelFor={this.firstDayOfWeekId} description="The first day of the week in calendars.">
                    <b-form-radio-group id={this.firstDayOfWeekId} class="first-day-of-week p-2 border rounded" stacked checked={this.firstDayOfWeek} name="first-day-of-week" options={this.sortedWeekdays.map(({ name, value }) => ({
                    text: name,
                    value,
                }))} disabled={this.setPromise.isJust()} onChange={(firstDayOfWeek) => {
                    this.update({ firstDayOfWeek });
                }}/>

                    <div slot="label">
                        First day of week{' '}
                        {utils.ifJustOrEmpty(this.setPromise, promise => (<PromiseLoader promise={promise} popoverPlacement="top" onAfterSuccess={() => {
                        this.setPromise = Maybe.empty();
                    }} onAfterError={() => {
                        this.setPromise = Maybe.empty();
                    }} doRetry={() => {
                        // When storing the setting failed, it is still
                        // updated in the store, so we don't need to
                        // keep track of which values have changed and
                        // pass those to `update` here.
                        this.update({});
                    }}/>))}
                    </div>
                </b-form-group>);
        },
        /** @returns The calendar preview. */
        calendarNode() {
            return (<b-form-group label="Preview" description="This is a preview of the calendar.">
                    <FlatPickr ref="flatpickr-calendar" class="form-control" value={new Date()} config={this.flatpickrConfig}/>
                </b-form-group>);
        },
        /** @returns The popover to show when hovering the button. */
        buttonPopover() {
            return (<b-popover target={this.buttonId} triggers="hover focus" placement="top" customClass="text-left" variant={this.loadError.mapOrDefault(() => 'danger', 'secondary')}>
                    {this.loadError.caseOf({
                    Nothing: () => 'Calendar settings',
                    Just: ({ error, retry }) => (<div class="clearfix">
                                <p class="mb-2">
                                    The calendar settings could not be loaded: {error.message}
                                </p>
                                <b-button size="sm" variant="danger" onClick={() => retry()} class="float-right">
                                    Retry
                                </b-button>
                            </div>),
                })}
                </b-popover>);
        },
    },
    methods: {
        /**
         * Update the given values in the configuration.
         *
         * @param value The values to update.
         */
        update(value) {
            this.setPromise = Maybe.of(this.calendarConfig.set({
                ...this.calendarConfig.get(),
                ...value,
            }));
        },
    },
    /** @returns The rendered component. */
    render() {
        return (<b-button id={this.buttonId} class="calendar-settings" v-b-modal={this.modalId} variant={this.loadError.mapOrDefault(() => 'outline-danger', 'secondary')}>
                {utils.ifExpr(this.loading, () => (<Loader scale={1}/>), () => [<Icon icon={Settings}/>, this.buttonPopover, this.modalNode])}
            </b-button>);
    },
});
